/**
 * Build device attention issues (PPM, calibration, warranty, contract, complaints).
 * Shared by device view and edit form banners.
 *
 * @param {object} deviceData
 * @param {object|null} user - current user (technician complaint wording differs)
 */
export function getDeviceIssues(deviceData, user = null) {
  const critical = [];
  const attention = [];
  const upcoming = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return new Date(dateStr);
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day, 0, 0, 0, 0);
  };

  const daysUntil = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date) return null;
    return Math.round((date - today) / (1000 * 60 * 60 * 24));
  };

  const ppmDays = daysUntil(deviceData.ppm_next_due_date);
  if (ppmDays !== null) {
    if (ppmDays < 0) {
      critical.push({
        key: 'ppm-overdue',
        chip: 'PPM overdue',
        detail: `PPM is ${Math.abs(ppmDays)} day${Math.abs(ppmDays) !== 1 ? 's' : ''} overdue`,
      });
    } else if (ppmDays <= 7) {
      upcoming.push({
        key: 'ppm-soon',
        chip: 'PPM due soon',
        detail: `PPM due in ${ppmDays} day${ppmDays !== 1 ? 's' : ''}`,
      });
    }
  }

  const calibrationDays = daysUntil(deviceData.calibration_next_due_date);
  if (calibrationDays !== null) {
    if (calibrationDays < 0) {
      critical.push({
        key: 'cal-overdue',
        chip: 'Calibration overdue',
        detail: `Calibration is ${Math.abs(calibrationDays)} day${Math.abs(calibrationDays) !== 1 ? 's' : ''} overdue`,
      });
    } else if (calibrationDays <= 7) {
      upcoming.push({
        key: 'cal-soon',
        chip: 'Calibration due soon',
        detail: `Calibration due in ${calibrationDays} day${calibrationDays !== 1 ? 's' : ''}`,
      });
    }
  }

  const warrantyDays = daysUntil(deviceData.warranty_expiry);
  if (warrantyDays !== null) {
    if (warrantyDays < 0) {
      attention.push({
        key: 'warranty-expired',
        chip: 'Warranty expired',
        detail: `Warranty expired ${Math.abs(warrantyDays)} day${Math.abs(warrantyDays) !== 1 ? 's' : ''} ago`,
      });
    } else if (warrantyDays <= 7) {
      upcoming.push({
        key: 'warranty-soon',
        chip: 'Warranty ending',
        detail: `Warranty expires in ${warrantyDays} day${warrantyDays !== 1 ? 's' : ''}`,
      });
    }
  }

  const contractDays = daysUntil(deviceData.contract_expiry);
  if (contractDays !== null) {
    if (contractDays < 0) {
      attention.push({
        key: 'contract-expired',
        chip: 'Contract expired',
        detail: `Service contract expired ${Math.abs(contractDays)} day${Math.abs(contractDays) !== 1 ? 's' : ''} ago`,
      });
    } else if (contractDays <= 7) {
      upcoming.push({
        key: 'contract-soon',
        chip: 'Contract ending',
        detail: `Service contract expires in ${contractDays} day${contractDays !== 1 ? 's' : ''}`,
      });
    }
  }

  const activeComplaintCount = deviceData.active_complaint_count || 0;
  const criticalComplaintCount = deviceData.critical_active_complaint_count || 0;
  const assignedToMeCount = deviceData.assigned_to_me_active_complaint_count || 0;
  const assignedToMeCriticalCount =
    deviceData.assigned_to_me_critical_active_complaint_count || 0;
  const isTechnicianUser = user?.role === 'technician';

  if (activeComplaintCount > 0) {
    if (isTechnicianUser) {
      const assignedNoun =
        assignedToMeCount === 1 ? 'complaint assigned' : 'complaints assigned';
      const assignedLabel = `${assignedToMeCount} ${assignedNoun}`;
      const activeLabel = `${activeComplaintCount} active`;
      const chip = `${assignedLabel} (${activeLabel})`;

      const assignedDetail =
        assignedToMeCriticalCount > 0
          ? `${assignedLabel} (${assignedToMeCriticalCount} critical)`
          : assignedLabel;
      const activeComplaintsLabel =
        activeComplaintCount === 1
          ? '1 active complaint'
          : `${activeComplaintCount} active complaints`;
      const activeDetail =
        criticalComplaintCount > 0
          ? `${activeComplaintsLabel} (${criticalComplaintCount} critical)`
          : activeComplaintsLabel;
      const detail = `${assignedDetail} · ${activeDetail}`;

      const complaintIssue = {
        key: 'complaints-active',
        chip,
        detail,
        isComplaint: true,
        showComplaintNav: assignedToMeCount > 0,
      };
      if (criticalComplaintCount > 0) {
        critical.push(complaintIssue);
      } else {
        attention.push(complaintIssue);
      }
    } else if (criticalComplaintCount > 0) {
      critical.push({
        key: 'complaints-critical',
        chip:
          criticalComplaintCount === 1
            ? '1 critical complaint'
            : `${criticalComplaintCount} critical complaints`,
        detail:
          criticalComplaintCount === 1
            ? '1 critical active complaint on this device'
            : `${criticalComplaintCount} critical active complaints on this device`,
        isComplaint: true,
        showComplaintNav: true,
        complaintNavSeverities: ['critical'],
      });
      const otherComplaints = activeComplaintCount - criticalComplaintCount;
      if (otherComplaints > 0) {
        attention.push({
          key: 'complaints-other',
          chip:
            otherComplaints === 1
              ? '1 other active complaint'
              : `${otherComplaints} other active complaints`,
          detail:
            otherComplaints === 1
              ? '1 additional active complaint (non-critical)'
              : `${otherComplaints} additional active complaints (non-critical)`,
          isComplaint: true,
          showComplaintNav: true,
          complaintNavSeverities: ['major', 'minor'],
        });
      }
    } else {
      attention.push({
        key: 'complaints-active',
        chip:
          activeComplaintCount === 1
            ? '1 active complaint'
            : `${activeComplaintCount} active complaints`,
        detail:
          activeComplaintCount === 1
            ? '1 active complaint on this device'
            : `${activeComplaintCount} active complaints on this device`,
        isComplaint: true,
        showComplaintNav: true,
      });
    }
  }

  const severity =
    critical.length > 0
      ? 'critical'
      : attention.length > 0
        ? 'attention'
        : upcoming.length > 0
          ? 'upcoming'
          : null;

  return {
    critical,
    attention,
    upcoming,
    severity,
    chips: [...critical, ...attention, ...upcoming],
    details: [...critical, ...attention, ...upcoming],
  };
}
